/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file about this app section for Showroom
 * @module Epic.AppOrchard.Showroom.AboutThisAppSection
 */

import {
	Box,
	Button,
	Flex,
	Heading,
	Switch,
	Text,
	useDisclosure,
	useToast,
	Wrap,
	WrapItem,
} from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { showroomText } from "ao/chakraTheme";
import { AOLink, ConfirmationModal, LinkButton } from "ao/components/Core";
import { setDuqVisibility } from "ao/components/DownloadApp/data/data";
import { getConsumerTypeName } from "ao/components/Showroom/Helper";
import { DownloadApprovedStatus, QuestionnaireType } from "ao/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IUserSecurity } from "ao/types";
import { DUQVisibility } from "ao/types/questionnaireVersion";
import { IGalleryApi, IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl, isLocalhost } from "ao/utils/helpers";
import { FunctionComponent, memo, useMemo } from "react";
import { ApiCollapse, ApiLink } from "../AppHelperComponents";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	buttonBackgroundColor: string;
	buttonBorderColor: string;
	textColor: string;
	isShowroom?: boolean;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
}

export const ShowroomAboutThisAppSection: FunctionComponent<IProps> = memo((props) => {
	const {
		appDetails,
		userSecurity,
		buttonBackgroundColor,
		buttonBorderColor,
		textColor,
		isShowroom,
		setAppDetails,
	} = props;

	const toast = useToast();

	const appModel = appDetails.appModel;

	const isEpicCustomerOrIsEpicUser = userSecurity.isEpicCustomer || userSecurity.isEpicUser;

	const isAppFromOwnOrg =
		userSecurity.userOrganizationId != null &&
		userSecurity.userOrganizationId === appModel.organizationId;

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);
	const isShowroomPublic = isShowroom && !isLocalhost() && !siteInfo.isAdminSite;

	const baseUrl = useMemo(() => (isShowroomPublic ? siteInfo.vendorServicesUrl : getBaseUrl()), [
		isShowroomPublic,
		siteInfo.vendorServicesUrl,
	]);

	const duqLink = isShowroom
		? `${siteInfo.showroomRelativeUrl}Questionnaire?id=${appDetails.appModel.id}&type=${QuestionnaireType.DataUse}`
		: `/Download/QuestionnaireView?id=${appDetails.appModel.id}&type=${QuestionnaireType.DataUse}`;
	const arqLink = isShowroom
		? `${siteInfo.showroomRelativeUrl}Questionnaire?id=${appDetails.appModel.id}&type=${QuestionnaireType.AppReadiness}`
		: `/Download/QuestionnaireView?id=${appDetails.appModel.id}&type=${QuestionnaireType.AppReadiness}`;

	const duqLinkContent = (
		<Box>
			<Text fontWeight="500" fontSize="1.25rem">
				Data Use
			</Text>
			<Text
				color={showroomText}
				fontStyle="italic"
				fontWeight="400"
				width="100%"
				textAlign="center"
				height="20px"
				m="0"
				alignContent="center"
			>
				Patient data handling
			</Text>
		</Box>
	);

	const arqLinkContent = (
		<Box>
			<Text fontWeight="500" fontSize="1.25rem">
				App Readiness
			</Text>
			<Text
				color={showroomText}
				fontStyle="italic"
				fontWeight="400"
				width="100%"
				textAlign="center"
				height="20px"
				m="0"
				alignContent="center"
			>
				General safety, privacy, security, etc practices
			</Text>
		</Box>
	);

	const hasIntegration =
		appModel.incomingApis?.length > 0 ||
		appModel.kitApiGroups?.length > 0 ||
		appModel.subspaceEvents?.length > 0 ||
		appModel.outgoingFunctionalities?.length > 0 ||
		appModel.contentSchema !== null;

	const canViewQuestionnaires =
		isAppFromOwnOrg ||
		(userSecurity.isEpicCustomer && userSecurity.canPurchase) ||
		userSecurity.isEpicUser;
	const hasQuestionnaires = appDetails.showDUQInListing || appDetails.showARQInListing;

	const {
		isOpen: isDuqVisibilityConfirmationOpen,
		onOpen: onOpenDuqVisibilityConfirmation,
		onClose: onCloseDuqVisibilityConfirmation,
	} = useDisclosure();

	const [, updateDuqVisibility] = useAsync(setDuqVisibility, {
		executeImmediately: false,
		displayName: "loadAssociations",
		onSuccess: () => {},
		onFailure: () => {
			toast({
				title: "Error",
				description: "Failed to update the visibility of the Data Use Questionnaire. Try again.",
				status: "error",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
	});

	return (
		<>
			{canViewQuestionnaires && hasQuestionnaires && (
				<Box>
					<Heading
						as="h3"
						fontSize="1.3rem"
						fontWeight="600"
						textColor={textColor}
						width="100%"
						textAlign="center"
					>
						Product Questionnaires
					</Heading>
					<Text color={showroomText} w="100%" textAlign="center">
						These questionnaires are optional and completed by the vendor
					</Text>

					<Wrap spacing="35px" pt="20px" justify="center" w="100%">
						{appDetails.showDUQInListing && (
							<WrapItem w="300px">
								<Box textAlign="center">
									{isShowroom ? (
										<LinkButton
											backgroundColor={buttonBackgroundColor}
											border={`solid 1px ${buttonBorderColor}`}
											boxShadow="0px 0px 2px 1px #c5c5c5"
											url={duqLink}
											w="300px"
											h="unset"
											p="8px 0 5px"
											title="View this optional, vendor-completed, questionnaire about how this product handles patient data"
										>
											{duqLinkContent}
										</LinkButton>
									) : (
										<Button
											as={AOLink}
											variant="outline"
											colorScheme="blue"
											url={duqLink}
											w="300px"
											h="unset"
											p="8px 0 5px"
											title="View this optional, vendor-completed, questionnaire about how this product handles patient data"
										>
											{duqLinkContent}
										</Button>
									)}

									{appDetails.appId !== undefined &&
										siteInfo.isCustomerDUQAttestationEnabled &&
										userSecurity.canPurchase &&
										appDetails.isDownloaded &&
										!isAppFromOwnOrg &&
										appDetails.vendorApproved !== DownloadApprovedStatus.Declined &&
										appDetails.vendorApproved !== DownloadApprovedStatus.Overwritten && (
											<Box mt="5px">
												{appDetails.duqVisibilityOverride === DUQVisibility.Unset ? (
													<>
														<Switch
															isChecked={
																appDetails.duqVisibility ===
																	DUQVisibility.Unset ||
																appDetails.duqVisibility ===
																	DUQVisibility.ShowDataUse
															}
															onChange={() => {
																onOpenDuqVisibilityConfirmation();
															}}
															title="Update Data Use visibility"
														>
															{appDetails.duqVisibility ===
															DUQVisibility.DoNotShow
																? "Do not show Data Use answers to patients."
																: "Show Data Use answers to patients."}
														</Switch>

														<ConfirmationModal
															title="Display DUQ Language for Third Parties (Except Contracted Vendors and Business Associates)"
															isCentered
															message={
																appDetails.duqVisibility ===
																	DUQVisibility.Unset ||
																appDetails.duqVisibility ===
																	DUQVisibility.ShowDataUse ? (
																	<>
																		<Text mb="1rem">
																			The MyChart Data Use Questionnaire
																			is intended to help patients
																			understand how a third-party app
																			will use their health information
																			so they can make informed
																			decisions about sharing their
																			health information with the app.
																		</Text>
																		<Text mb="1rem">
																			The Questionnaire should appear
																			with all third-party apps except
																			those published by organizations
																			you have contracts with to address
																			your privacy obligations under the
																			laws and regulations that apply to
																			you (such as HIPAA, GDPR, PHIA).
																		</Text>
																		<Text>
																			This setting is only used by Epic
																			August 2025 and later versions.
																		</Text>
																	</>
																) : (
																	<>
																		<Text mb="1rem">
																			Are you sure you want to start
																			showing patients this app's
																			answers to the Data Use
																			Questionnaire?
																		</Text>
																		<Text>
																			This setting is only used by Epic
																			August 2025 and later versions.
																		</Text>
																	</>
																)
															}
															acceptCaption="Confirm"
															isOpen={isDuqVisibilityConfirmationOpen}
															onCancel={onCloseDuqVisibilityConfirmation}
															onAccept={() => {
																const newVisibility =
																	appDetails.duqVisibility ===
																		DUQVisibility.Unset ||
																	appDetails.duqVisibility ===
																		DUQVisibility.ShowDataUse
																		? DUQVisibility.DoNotShow
																		: DUQVisibility.ShowDataUse;
																setAppDetails({
																	...appDetails,
																	duqVisibility: newVisibility,
																});
																updateDuqVisibility(
																	appDetails.appId!,
																	newVisibility,
																);
																onCloseDuqVisibilityConfirmation();
															}}
														/>
													</>
												) : (
													<Text title="Contact Vendor Services if this is not appropriate for your organization.">
														{appDetails.duqVisibilityOverride ===
														DUQVisibility.DoNotShow
															? "Do not show Data Use answers to patients."
															: "Show Data Use answers to patients."}
													</Text>
												)}
											</Box>
										)}
								</Box>
							</WrapItem>
						)}

						{appDetails.showARQInListing && (
							<WrapItem w="300px">
								<Box textAlign="center">
									{isShowroom ? (
										<LinkButton
											backgroundColor={buttonBackgroundColor}
											border={`solid 1px ${buttonBorderColor}`}
											boxShadow="0px 0px 2px 1px #c5c5c5"
											url={arqLink}
											w="300px"
											h="unset"
											p="8px 0 5px"
											title="View this optional, vendor-completed, questionnaire about how this product handles general safety, privacy, and security practices"
										>
											{arqLinkContent}
										</LinkButton>
									) : (
										<Button
											as={AOLink}
											variant="outline"
											colorScheme="blue"
											url={arqLink}
											w="300px"
											h="unset"
											p="8px 0 5px"
											title="View this optional, vendor-completed, questionnaire about how this product handles general safety, privacy, and security practices"
										>
											{arqLinkContent}
										</Button>
									)}
								</Box>
							</WrapItem>
						)}
					</Wrap>
				</Box>
			)}

			{(isEpicCustomerOrIsEpicUser || isAppFromOwnOrg) && (
				<Box>
					<Heading
						as="h3"
						fontSize="1.3rem"
						fontWeight="600"
						textColor={textColor}
						width="100%"
						textAlign="center"
					>
						Consumer Type
					</Heading>
					<Text textAlign="center">{getConsumerTypeName(appModel.consumerType)}</Text>
				</Box>
			)}

			{(isEpicCustomerOrIsEpicUser || isAppFromOwnOrg) && hasIntegration && (
				<>
					{appModel.incomingApis.length > 0 && (
						<ApiCollapse
							label="Incoming APIs Used"
							textColor={textColor}
							childCount={appModel.incomingApis.length}
						>
							{appModel.incomingApis.map((api: IGalleryApi) => (
								<ApiLink key={api.id} api={api} baseUrl={baseUrl} color="#0000ff" />
							))}
						</ApiCollapse>
					)}
					{appModel.kitApiGroups.length > 0 && (
						<ApiCollapse
							label="Kit APIs Used"
							textColor={textColor}
							childCount={appModel.kitApiGroups.length}
						>
							{appModel.kitApiGroups.map((api: IGalleryApi) => (
								<ApiLink key={api.id} api={api} baseUrl={baseUrl} color="#0000ff" />
							))}
						</ApiCollapse>
					)}
					{appModel.subspaceEvents.length > 0 && (
						<ApiCollapse
							label="Subspace Events Used"
							textColor={textColor}
							childCount={appModel.subspaceEvents.length}
						>
							{appModel.subspaceEvents.map((api: IGalleryApi) => (
								<Flex
									justify="center"
									key={`subspace${api.id}`}
									noOfLines={1}
									wordBreak="break-all"
									textAlign="center"
								>
									<Text>{api.name}</Text>
								</Flex>
							))}
						</ApiCollapse>
					)}
					{appModel.outgoingFunctionalities.length > 0 && (
						<ApiCollapse
							label="Outgoing Functionalities Used"
							textColor={textColor}
							childCount={appModel.outgoingFunctionalities.length}
						>
							{appModel.outgoingFunctionalities.map((api: IGalleryApi) => (
								<ApiLink key={api.id} api={api} baseUrl={baseUrl} color="#0000ff" />
							))}
						</ApiCollapse>
					)}
					{appModel.contentSchema !== null && (
						<ApiCollapse label="Content Schema" textColor={textColor} childCount={1}>
							<Flex justify="center">
								<Text>{appModel.contentSchema.name}</Text>
							</Flex>
						</ApiCollapse>
					)}
				</>
			)}
		</>
	);
});
